import React, { useState, useEffect } from 'react';
import { X, Briefcase, Loader2 } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';
import { useNotification } from '../context/NotificationContext';
import SupervisorSelect from './SupervisorSelect';

const BatchAssignPositionModal = ({ selectedIds, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { success: notifySuccess, error: notifyError } = useNotification();

  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [filteredPositions, setFilteredPositions] = useState([]);

  const [selectedDept, setSelectedDept] = useState('');
  const [selectedPosition, setSelectedPosition] = useState('');
  const [selectedSupervisor, setSelectedSupervisor] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch('/api/organization/departments').then(r => r.json()),
      fetch('/api/organization/positions').then(r => r.json()),
    ]).then(([deptRes, posRes]) => {
      if (deptRes.status === 'success') setDepartments(deptRes.data);
      if (posRes.status === 'success') setPositions(posRes.data);
    }).catch(err => console.error('Failed to load:', err))
    .finally(() => setLoading(false));
  }, []);

  // Filter positions when department changes
  useEffect(() => {
    if (!selectedDept) {
      setFilteredPositions([]);
      setSelectedPosition('');
      return;
    }
    const filtered = positions.filter(p => Number(p.department_id) === Number(selectedDept));
    setFilteredPositions(filtered);
    // Reset position selection if current selection not in filtered
    if (selectedPosition && !filtered.some(p => Number(p.id) === Number(selectedPosition))) {
      setSelectedPosition('');
    }
  }, [selectedDept, positions]);

  const handleSubmit = async () => {
    if (!selectedPosition) {
      notifyError(t('employees.select_position_hint'));
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch('/api/employees/batch-assign-position', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ids: selectedIds,
          position_id: selectedPosition,
          supervisor_id: selectedSupervisor || null,
        }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        notifySuccess(data.message || t('common.success'));
        if (onSuccess) onSuccess();
        onClose();
      } else {
        notifyError(data.message || t('common.error'));
      }
    } catch (err) {
      notifyError(t('common.network_error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 w-full max-w-lg mx-4 animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex justify-between items-center p-5 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-lg font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
            <Briefcase size={20} className="text-blue-500" />
            {t('employees.batch_assign_position')}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        {loading ? (
          <div className="flex items-center justify-center p-10">
            <Loader2 size={24} className="animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="p-5 space-y-5">
            {/* Hint: selected count */}
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t('employees.batch_assign_selected_count', { count: selectedIds.length })}
            </p>

            {/* Department select */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                {t('employees.department')} <span className="text-red-500">*</span>
              </label>
              <select
                className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-transparent dark:text-white"
                value={selectedDept}
                onChange={e => setSelectedDept(e.target.value)}
              >
                <option value="">{t('employees.select_department_hint')}</option>
                {departments.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>

            {/* Position select */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                {t('employees.position')} <span className="text-red-500">*</span>
              </label>
              <select
                className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-transparent dark:text-white"
                value={selectedPosition}
                onChange={e => setSelectedPosition(e.target.value)}
                disabled={!selectedDept}
              >
                <option value="">
                  {selectedDept ? t('employees.select_position_hint') : t('employees.select_dept_first')}
                </option>
                {filteredPositions.map(p => (
                  <option key={p.id} value={p.id}>{p.name} {p.code ? `(${p.code})` : ''}</option>
                ))}
              </select>
            </div>

            {/* Supervisor select */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                {t('employees.supervisor')}
              </label>
              <SupervisorSelect
                value={selectedSupervisor}
                onChange={setSelectedSupervisor}
              />
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end gap-3 p-5 border-t border-slate-200 dark:border-slate-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !selectedPosition}
            className="px-5 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
          >
            {isSubmitting ? (
              <><Loader2 size={14} className="animate-spin" /> {t('common.processing')}</>
            ) : (
              <><Briefcase size={14} /> {t('common.confirm')}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BatchAssignPositionModal;
